import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Typography,
  Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpportunityList from './components/OpportunityList';
import OpportunityDetail from './components/OpportunityDetail';
import OpportunityForm from './components/OpportunityForm';
import OpportunityImport from './components/OpportunityImport';
import {
  getOpportunities,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  uploadOpportunities,
} from '../../api/opportunity.api';

function OpportunitiesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Load opportunities on component mount
  useEffect(() => {
    loadOpportunities();
  }, []);

  const loadOpportunities = async () => {
    setLoading(true);
    try {
      const data = await getOpportunities();
      setOpportunities(data);
    } catch (error) {
      showSnackbar('Failed to load opportunities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingOpportunity(null);
    setAddDialogOpen(true);
  };

  const handleEdit = (opportunity) => {
    setEditingOpportunity(opportunity);
    setEditDialogOpen(true);
  };

  const handleDelete = async (opportunity) => {
    if (window.confirm('Are you sure you want to delete this opportunity?')) {
      try {
        await deleteOpportunity(opportunity._id);
        await loadOpportunities();
        if (selectedOpportunity?._id === opportunity._id) {
          setSelectedOpportunity(null);
        }
        showSnackbar('Opportunity deleted successfully', 'success');
      } catch (error) {
        showSnackbar('Failed to delete opportunity', 'error');
      }
    }
  };

  const handleView = (opportunity) => {
    setSelectedOpportunity(opportunity);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when opportunity is selected
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingOpportunity) {
        await updateOpportunity(editingOpportunity._id, formData);
        showSnackbar('Opportunity updated successfully', 'success');
      } else {
        await createOpportunity(formData);
        showSnackbar('Opportunity created successfully', 'success');
      }

      await loadOpportunities();
      setAddDialogOpen(false);
      setEditDialogOpen(false);
      setEditingOpportunity(null);
    } catch (error) {
      showSnackbar('Failed to save opportunity', 'error');
    }
  };

  const handleImport = () => {
    setImportDialogOpen(true);
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    loadOpportunities();
    showSnackbar('Opportunities imported successfully', 'success');
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setEditingOpportunity(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingOpportunity(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const opportunitiesWithId = (opportunities || []).map((opportunity) => ({
    ...opportunity,
    id: opportunity._id || opportunity.id,
  }));

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Opportunities
            </Typography>
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedOpportunity ? (
            // Opportunity Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <OpportunityDetail
                    opportunity={selectedOpportunity}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select an Opportunity
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose an opportunity from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Opportunity List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <OpportunityList
                opportunities={opportunitiesWithId}
                loading={loading}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onImport={handleImport}
                selectedOpportunity={selectedOpportunity}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Opportunity Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Opportunity
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <OpportunityForm
              opportunity={null}
              onSave={handleSave}
              onCancel={handleCloseAddDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Edit Opportunity Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Edit Opportunity
            </Typography>
            <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <OpportunityForm
              opportunity={editingOpportunity}
              onSave={handleSave}
              onCancel={handleCloseEditDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Import Dialog */}
        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Import Opportunities
            </Typography>
            <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <OpportunityImport
              onClose={() => setImportDialogOpen(false)}
              onImportSuccess={handleImportSuccess}
            />
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Opportunities
      </Typography>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Opportunity List */}
        <Grid item xs={12} md={selectedOpportunity ? 8 : 12}>
          <Paper sx={{
            height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
          }}
          >
            <OpportunityList
              opportunities={opportunitiesWithId}
              loading={loading}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              onImport={handleImport}
              selectedOpportunity={selectedOpportunity}
            />
          </Paper>
        </Grid>

        {/* Opportunity Detail */}
        {selectedOpportunity && (
          <Grid item xs={12} md={4}>
            <Paper sx={{
              height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
            }}
            >
              <OpportunityDetail
                opportunity={selectedOpportunity}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add Opportunity Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Opportunity
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <OpportunityForm
            opportunity={null}
            onSave={handleSave}
            onCancel={handleCloseAddDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Edit Opportunity Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Edit Opportunity
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <OpportunityForm
            opportunity={editingOpportunity}
            onSave={handleSave}
            onCancel={handleCloseEditDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Import Opportunities
          </Typography>
          <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <OpportunityImport
            onClose={() => setImportDialogOpen(false)}
            onImportSuccess={handleImportSuccess}
          />
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OpportunitiesPage;
